import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface ViduTemplateParams {
  /**
   * 模板名称，必填参数。支持的模板: turn_into_zombie (变成僵尸效果), head_to_balloon (头部变气球效果), wednesdays_vibe (星期三氛围效果), covered_liquid_metal (液态金属覆盖效果)
   * @values turn_into_zombie | head_to_balloon | wednesdays_vibe | covered_liquid_metal
   */
  template: 'turn_into_zombie' | 'head_to_balloon' | 'wednesdays_vibe' | 'covered_liquid_metal';
  /**
   * 输入图片，必填参数。至少需要1张图片。支持URL (http://或https://开头) 或base64编码 (data:image/开头)
   */
  images: string | string[];
  /**
   * 文本提示词，可选参数。用于指导视频生成效果。长度不超过2000字符
   * @length 0 - 2000
   */
  prompt?: string;
  /**
   * 随机种子，可选参数。用于控制生成结果的随机性，相同种子会生成相似结果
   */
  seed?: number;
  /**
   * 是否添加背景音乐，可选参数。默认为false
   */
  bgm?: boolean;
  /**
   * 额外参数，可选参数。用于传递模板特定的扩展配置
   */
  extra_params?: Record<string, any>;
}

export interface ViduTemplateResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * vidu_template
 *
 * API Documentation: https://api.vidu.com/ent/v2/template
 * 
 * Vidu 模板视频生成实现。支持变成僵尸、头部变气球、星期三氛围、液态金属覆盖等多种模板效果。固定4秒时长，720p分辨率。
 *
 * @example
 * ```typescript
 * import { initSeacloud, viduTemplate } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await viduTemplate({
 *   // 参数见 ViduTemplateParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 ViduTemplateParams 接口定义
 * @returns 生成的资源列表
 */
export async function viduTemplate(
  params: ViduTemplateParams,
): Promise<ViduTemplateResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'vidu_template',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: ViduTemplateResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
